import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";

const ICON_STROKE = 1.5;

const MAIN_NAV = [
  { label: "Home", to: "/" },
  { label: "About & vision", to: "/#about" },
  { label: "Programs", to: "/#programs" },
  { label: "Master programs", to: "/#master-programs" },
  { label: "Contact", to: "/#contact" },
];

function isMainNavActive(location, to) {
  const hashIdx = to.indexOf("#");
  const path = hashIdx === -1 ? to : to.slice(0, hashIdx);
  const wantHash = hashIdx === -1 ? null : `#${to.slice(hashIdx + 1)}`;

  if (location.pathname !== path) return false;

  if (path === "/" && wantHash === null) {
    return !location.hash;
  }

  if (wantHash !== null) {
    return location.hash === wantHash;
  }

  return true;
}

export function SiteHeader({ uni, logoSrc }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const linkClass = (active) =>
    [
      "rounded-lg px-3 py-2 text-sm font-medium transition",
      active ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white",
    ].join(" ");

  const mobileLinkClass = (active) =>
    [
      "rounded-xl px-4 py-3 text-base font-medium",
      active ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/5",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 shadow-md shadow-black/10">
      <div className="border-b border-white/10 bg-ink-900 text-slate-100">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
          <Link
            to="/"
            className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4"
          >
            <img
              src={logoSrc}
              alt=""
              className="h-11 w-11 shrink-0 rounded-xl bg-white/10 object-contain ring-1 ring-white/10 sm:h-14 sm:w-14"
              width={56}
              height={56}
              decoding="async"
            />
            <div className="min-w-0 text-left">
              <p className="truncate text-sm font-bold leading-tight text-white sm:text-base">
                {uni.shortName}
              </p>
              <p className="mt-0.5 text-xs leading-snug text-slate-400 sm:text-sm">
                {uni.tagline}
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {MAIN_NAV.map((item) => {
              const active = isMainNavActive(location, item.to);
              return (
                <Link key={item.to} to={item.to} className={linkClass(active)}>
                  {item.label}
                </Link>
              );
            })}
            <NavLink
              to="/student-results"
              className={({ isActive }) =>
                [
                  "ml-2 inline-flex items-center rounded-lg border px-3 py-2 text-sm font-semibold transition",
                  isActive
                    ? "border-maroon-400 bg-maroon-900/80 text-white"
                    : "border-maroon-600/60 bg-maroon-900/40 text-maroon-50 hover:border-maroon-500 hover:bg-maroon-900/70",
                ].join(" ")
              }
            >
              Student results
            </NavLink>
            <NavLink
              to="/apply"
              className={({ isActive }) =>
                [
                  "ml-2 inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-md shadow-maroon-900/30 transition",
                  isActive ? "bg-maroon-600" : "bg-maroon-700 hover:bg-maroon-600",
                ].join(" ")
              }
            >
              Apply now
            </NavLink>
          </nav>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? (
              <X className="h-6 w-6" strokeWidth={ICON_STROKE} aria-hidden />
            ) : (
              <Menu className="h-6 w-6" strokeWidth={ICON_STROKE} aria-hidden />
            )}
            <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
          </button>
        </div>

        <div
          id="mobile-nav"
          className={`border-t border-white/10 bg-ink-900 lg:hidden ${menuOpen ? "block" : "hidden"}`}
        >
          <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-4 sm:px-6">
            {MAIN_NAV.map((item) => {
              const active = isMainNavActive(location, item.to);
              return (
                <Link key={`m-${item.to}`} to={item.to} className={mobileLinkClass(active)}>
                  {item.label}
                </Link>
              );
            })}
            <NavLink
              to="/student-results"
              className={({ isActive }) =>
                [
                  "rounded-xl px-4 py-3 text-base font-semibold",
                  isActive
                    ? "bg-maroon-900 text-white"
                    : "bg-maroon-900/50 text-maroon-50 hover:bg-maroon-900/80",
                ].join(" ")
              }
            >
              Student results & transcript
            </NavLink>
            <NavLink
              to="/apply"
              className={({ isActive }) =>
                [
                  "rounded-xl px-4 py-3 text-base font-semibold text-white shadow-md shadow-maroon-900/30",
                  isActive ? "bg-maroon-600" : "bg-maroon-700 hover:bg-maroon-600",
                ].join(" ")
              }
            >
              Apply now
            </NavLink>
          </nav>
        </div>
      </div>
    </header>
  );
}
